/** Thrown for any non-2xx response, carrying the message the API chose to show. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fieldErrors: string[] = [],
  ) {
    super(message);
  }
}

/**
 * A 401 means the session is over — announced, so the whole app can act on it at once.
 *
 * Before this, a session that ended while somebody was in the app (twelve hours up, signed in
 * on another device, password changed, account deactivated) went unnoticed. The shell stayed
 * on screen and every page failed one at a time with its own error, so it looked like the Hub
 * was broken rather than that they had been signed out.
 *
 * SAFE TO TREAT EVERY 401 THIS WAY, which was checked rather than assumed: a signed-in person
 * only ever gets a 401 from the session guard. The other 401s in the API are on the sign-in
 * steps themselves (a wrong password, a wrong second-step code), which happen with no session
 * — and the listener ignores the event when there is no session to end. A wrong CURRENT
 * password in Settings is a 400, not a 401, so a typo there does not sign anybody out.
 */
export const SESSION_ENDED_EVENT = 'hub:session-ended';

function announceIfSignedOut(status: number, message: string) {
  if (status === 401) {
    window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT, { detail: message }));
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    // Sends the httpOnly session cookie.
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload = text ? safeParse(text) : null;

  if (!res.ok) {
    // Nest returns `message` as a string, or an array of them for validation failures.
    const raw = payload?.message;
    const list = Array.isArray(raw) ? raw : [];
    const message = list[0] || (typeof raw === 'string' ? raw : null) || `Request failed (${res.status})`;
    announceIfSignedOut(res.status, message);
    throw new ApiError(res.status, message, list);
  }

  return payload as T;
}

/**
 * A multipart POST, for a file.
 *
 * Deliberately does NOT set Content-Type: the browser has to write it, because only the
 * browser knows the boundary it generated. Setting it by hand leaves the server unable to
 * find the parts, which reads as "no file was attached".
 */
async function upload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`/api${path}`, { method: 'POST', credentials: 'same-origin', body: form });

  const text = await res.text();
  const payload = text ? safeParse(text) : null;

  if (!res.ok) {
    const raw = payload?.message;
    const list = Array.isArray(raw) ? raw : [];
    const message = list[0] || (typeof raw === 'string' ? raw : null) || `Upload failed (${res.status})`;
    announceIfSignedOut(res.status, message);
    throw new ApiError(res.status, message, list);
  }

  return payload as T;
}

function safeParse(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  /**
   * A body is optional and rarely used, but a DELETE that has to be explained carries one —
   * the reason for removing a company or a project is required by the API and travels here.
   */
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
  upload: <T>(path: string, form: FormData) => upload<T>(path, form),
};
